import { create } from "zustand";
import type { components } from "@/types/api";

// Wire alias for the backend's UserSummary schema (embedded in
// LoginResponse). The frontend stores it as `AuthUser` for ergonomics.
export type UserSummaryWire = components["schemas"]["UserSummary"];

/**
 * The non-optional, always-populated view of the backend's UserSummary.
 * After a successful login the backend always returns every field; the
 * Zustand store narrows so consumers don't have to defensively `??`.
 */
export interface AuthUser {
  id: number;
  email: string;
  fullName: string;
  role: string;
  organizationId: number;
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  setAuth: (token: string, user: AuthUser) => void;
  setAccessToken: (token: string) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
}

/**
 * In-memory auth store. Access token is NOT persisted to localStorage
 * (XSS risk — see plan §"Auth flow"). The refresh token lives in an
 * HttpOnly cookie set by the backend and is sent automatically by the
 * browser; it is never visible to JavaScript.
 *
 * `setAuth` is used on login (token + user). `setAccessToken` is used by
 * the refresh interceptor — refresh rotates the access token but leaves
 * the user identity untouched.
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  setAuth: (token, user) => set({ accessToken: token, user }),
  setAccessToken: (token) => set({ accessToken: token }),
  clearAuth: () => set({ accessToken: null, user: null }),
  isAuthenticated: () => get().accessToken !== null,
}));
